import classNames from 'classnames';

import React, {
  FC,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
  RefAttributes,
} from 'react';

import type { Token as Color } from '../../../../export/brand0/colors';
import { Content } from '../content';
import { Divider } from '../divider';
import { Icon } from '../icon';
import { Image } from '../image';

import styles from './list-cell.scss';
import { flexAlignSelf } from './types';

type ListCellProps = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    text?: string;
    subtitle?: string;
    multiline?: boolean;
    icon?: string | ReactNode;
    content?: ReactNode;
    leftControl?: ReactNode;
    leftContent?: ReactNode;
    rightImage?: ReactNode;
    image?: ReactNode | string;
    leftImageSize?: number;
    withoutLeftImagePadding?: boolean;
    withoutRightImagePadding?: boolean;
    withContentDivider?: boolean;
    dividerHidden?: boolean;
    inverted?: boolean;
    leftContentAlignment?: flexAlignSelf;
    rightContentAlignment?: flexAlignSelf;
    leftContentOverflowVisible?: boolean;
    buttons?: ReactNode;
    bottomContainerContent?: string;
    onClick?: MouseEventHandler;
    dataTestId?: string;
    iconBackground?: Color;
  };

const COMPONENT_ID = 'modulor-list-cell';

const IconContainer: FC<
  Pick<ListCellProps, 'icon' | 'iconBackground' | 'dataTestId' | 'leftContentAlignment'>
> = ({ icon, dataTestId, iconBackground, leftContentAlignment }: ListCellProps) => {
  if (!icon) {
    return null;
  }
  const IconComponent =
    typeof icon === 'string' ? <Icon name={icon} dataTestId={dataTestId} /> : icon;

  return (
    <div
      className={classNames(styles.icon, {
        [styles.start]: leftContentAlignment === 'start',
        [styles['icon-with-bg']]: iconBackground,
      })}
      style={{
        background: iconBackground ? `var(--${iconBackground})` : 'unset',
        alignSelf: leftContentAlignment ? leftContentAlignment : 'auto',
      }}>
      {IconComponent}
    </div>
  );
};

const ImageContainer: FC<
  Pick<ListCellProps, 'image' | 'leftImageSize' | 'withoutLeftImagePadding' | 'dataTestId'>
> = ({ image, dataTestId, leftImageSize, withoutLeftImagePadding }: ListCellProps) => {
  if (!image) {
    return null;
  }
  return typeof image === 'string' ? (
    <Image name={image} src={image} dataTestId={dataTestId} width="24" height="24" />
  ) : (
    <div
      className={classNames({
        [styles['image-counter']]: withoutLeftImagePadding && leftImageSize < 56,
        [styles['large-image-counter']]: withoutLeftImagePadding && leftImageSize >= 56,
        [styles['image-counter-with-padding']]: !withoutLeftImagePadding && leftImageSize < 56,
        [styles['large-image-counter-with-padding']]:
          !withoutLeftImagePadding && leftImageSize >= 56,
      })}>
      {image}
    </div>
  );
};

export const ListCell: ForwardRefExoticComponent<ListCellProps> = forwardRef<
  HTMLDivElement,
  ListCellProps
>(
  (
    {
      text,
      subtitle,
      multiline,
      icon,
      content,
      image,
      leftImageSize,
      withoutLeftImagePadding,
      dividerHidden,
      withoutRightImagePadding,
      inverted,
      rightContentAlignment,
      buttons,
      leftContentAlignment,
      leftContentOverflowVisible,
      bottomContainerContent,
      leftControl,
      leftContent,
      rightImage,
      onClick,
      className,
      dataTestId = COMPONENT_ID,
      iconBackground,
      withContentDivider,
      ...rest
    },
    ref,
  ) => {
    return (
      <div
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        ref={ref}
        className={classNames(styles.container, className)}
        {...rest}>
        <div className={styles.element}>
          {bottomContainerContent ? (
            <div className={styles['content-bottom']}>{bottomContainerContent}</div>
          ) : null}
          <li
            onClick={onClick}
            data-testid={`${dataTestId}-li`}
            className={classNames(styles.list, {
              [styles.clickable]: !!onClick,
              [styles['without-left-padding']]: withoutLeftImagePadding,
              [styles['without-right-padding']]: withoutRightImagePadding,
            })}>
            {image ? (
              <div
                style={{
                  alignSelf: leftContentAlignment ? leftContentAlignment : 'auto',
                }}
                className={classNames(styles.image, {
                  [styles.start]: leftContentAlignment === 'start',
                  [styles.multiline]: multiline,
                  [styles['without-left-padding']]: withoutLeftImagePadding,
                })}>
                <ImageContainer
                  image={image}
                  leftImageSize={leftImageSize}
                  withoutLeftImagePadding={withoutLeftImagePadding}
                  dataTestId={`${dataTestId}-image`}
                />
              </div>
            ) : null}
            {icon ? (
              <IconContainer
                icon={icon}
                iconBackground={iconBackground}
                dataTestId={`${dataTestId}-icon`}
                leftContentAlignment={leftContentAlignment}
              />
            ) : null}
            {leftControl ? <div className={styles['left-control']}>{leftControl}</div> : null}
            {leftContent ? (
              <div
                className={classNames(styles.left, {
                  [styles.multiline]: multiline,
                  [styles.overflow]: leftContentOverflowVisible,
                })}>
                {leftContent}
              </div>
            ) : (
              <Content
                text={text}
                subtitle={subtitle}
                variant="body-regular"
                inverted={inverted}
                multiline={multiline}
                style={{
                  alignSelf: leftContentAlignment ? leftContentAlignment : 'auto',
                }}
                className={classNames(styles.left, {
                  [styles.start]: leftContentAlignment === 'start',
                  [styles.multiline]: multiline,
                })}
              />
            )}
            {content ? (
              <div
                style={{
                  alignSelf: rightContentAlignment ? rightContentAlignment : 'auto',
                }}
                className={classNames(styles.right, {
                  [styles.start]: rightContentAlignment === 'start',
                  [styles.multiline]: multiline,
                })}>
                {content}
              </div>
            ) : null}
            {rightImage ? (
              <div
                style={{
                  alignSelf: rightContentAlignment ? rightContentAlignment : 'auto',
                }}
                className={classNames(styles['right-image'], {
                  [styles.start]: rightContentAlignment === 'start',
                  [styles.multiline]: multiline,
                  [styles['without-right-padding']]: withoutRightImagePadding,
                })}>
                <ImageContainer image={rightImage} dataTestId={`${dataTestId}-right-image`} />
              </div>
            ) : null}
            {buttons ? (
              <>
                {withContentDivider ? <Divider className={styles['content-divider']} /> : null}
                <div
                  className={styles.buttons}
                  data-testid={`${dataTestId}-right-content-container`}>
                  {buttons}
                </div>
              </>
            ) : null}
            {!dividerHidden ? <Divider className={styles.divider} /> : null}
          </li>
        </div>
      </div>
    );
  },
);

ListCell.displayName = 'ListCell';
